<template>
  <div data-cy="box-catalog">
    <v-row class="mb-2">
      <v-col>
        <v-simple-table dense data-cy="box-sizes">
          <thead>
          <tr>
            <th>Box size</th>
            <th class="text-right">Per meal</th>
            <th>Delivery</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="boxSize in catalog.sizes" :key="boxSize.size">
            <td>
              {{ boxSize.size }} meals
              <span v-if="boxSize.size === catalog.defaultSize" class="grey--text">(default)</span>
            </td>
            <td class="text-right">{{ formatCurrency(boxSize.price) }}</td>
            <td>{{ boxSize.free_shipping ? 'free' : 'charged' }}</td>
          </tr>
          </tbody>
        </v-simple-table>
        <div v-if="largestSize && catalog.maxSize > largestSize" class="text-caption mt-1">
          Up to {{ catalog.maxSize }} meals in a box, priced as the box of {{ largestSize }}.
        </div>
      </v-col>
      <v-col cols="auto" class="d-flex align-end">
        <v-btn color="primary" outlined small @click="copyCatalogToClipboard">
          <v-icon left small>mdi-content-copy</v-icon>
          Copy Menu
        </v-btn>
      </v-col>
    </v-row>

    <v-tabs v-model="deliveryTab">
      <v-tab v-for="deliveryDate in catalog.deliveryDates" :key="deliveryDate">
        Delivery {{ dateFormatted(deliveryDate, {formatString: 'ddd MMM D'}) }}
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="deliveryTab">
      <v-tab-item v-for="deliveryDate in catalog.deliveryDates" :key="deliveryDate">
        <v-list>
          <v-list-item v-for="meal in mealsOf(deliveryDate)" :key="meal.id" data-cy="box-catalog-meal">
            <v-list-item-content>
              <v-list-item-title>{{ meal.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ describeKind(meal) }}</v-list-item-subtitle>
              <v-list-item-subtitle v-if="meal.ingredients && meal.ingredients.length > 0" class="mt-1 text-wrap">
                {{ meal.ingredients }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="meal.price_diff">
              <v-chip small>+{{ formatCurrency(meal.price_diff) }}</v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-alert v-if="mealsOf(deliveryDate).length === 0" type="info" outlined>
          No meals on the menu for this delivery.
        </v-alert>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import {dateFormatted, formatCurrency} from '@/store/utils';

const TOD_ORDER = {breakfast: 0, entree: 1};

// The week's box catalog of a customer on a box plan, as the api's
// v2/order-edit/:uid/:week/box-catalog answers it: the sizes the plan sells
// and, per delivery, the meals a box can hold with what each costs on top of
// the size's price per meal.
export default {
  name: 'BoxCatalogDisplay',
  props: {
    // {deliveryDates, sizes: [{size, price, free_shipping}], defaultSize, maxSize, meals: [{id, name, tod, streams, deliveryDates, chefs_choice, price_diff, ingredients}]}
    catalog: {type: Object, required: true},
  },
  data() {
    return {
      deliveryTab: 0,
    };
  },
  computed: {
    largestSize() {
      return Math.max(0, ...(this.catalog.sizes || []).map(boxSize => boxSize.size));
    },
  },
  methods: {
    dateFormatted,
    formatCurrency,
    // the meals of a delivery: the stream meals (breakfasts first), then the chef's choice products
    mealsOf(deliveryDate) {
      const sortKey = meal => `${meal.chefs_choice ? 1 : 0}-${TOD_ORDER[meal.tod] ?? 2}-${meal.name}`;
      return (this.catalog.meals || [])
        .filter(meal => (meal.deliveryDates || []).includes(deliveryDate))
        .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    },
    describeKind(meal) {
      if (meal.chefs_choice) {
        return ["Chef's Choice", meal.category].filter(s => !!s).join(' · ');
      }
      return [meal.tod, (meal.streams || []).join(', ')].filter(s => !!s).join(' · ');
    },
    formatCatalogAsText() {
      const lines = ['BOX MENU', ''];
      lines.push('Box sizes:');
      (this.catalog.sizes || []).forEach(boxSize => {
        const delivery = boxSize.free_shipping ? ', free delivery' : '';
        lines.push(`  ${boxSize.size} meals: ${formatCurrency(boxSize.price)} per meal${delivery}`);
      });
      (this.catalog.deliveryDates || []).forEach(deliveryDate => {
        lines.push('', `Delivery ${dateFormatted(deliveryDate)}:`);
        this.mealsOf(deliveryDate).forEach(meal => {
          const extraCost = meal.price_diff ? ` (+${formatCurrency(meal.price_diff)})` : '';
          lines.push(`  ${meal.name}${extraCost}`);
          if (meal.ingredients && meal.ingredients.length > 0) {
            lines.push(`    Ingredients: ${meal.ingredients}`);
          }
        });
      });
      return lines.join('\n');
    },
    async copyCatalogToClipboard() {
      try {
        await navigator.clipboard.writeText(this.formatCatalogAsText());
        this.$emit('copy-success', 'Menu copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy the box menu:', error);
        this.$emit('copy-error', 'Failed to copy menu to clipboard');
      }
    },
  },
}
</script>
